import React from 'react';
import {
  AppBar,
  Box,
  Drawer,
  FormControl,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import COLORS from '@/resources/style/Themes';
import { StringKeys } from '@/resources/strings/StringKeys';

type TabKey = 'pvd' | 'emergency';

const DRAWER_WIDTH = 240;

interface TabItem {
  key: TabKey;
  label: string;
}

interface ConfigurationsRouteBarProps {
  activeTab: TabKey;
  onTabChange: (tab: TabKey) => void;
}

const ConfigurationsRouteBar: React.FC<ConfigurationsRouteBarProps> = ({
  activeTab,
  onTabChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const tabs: TabItem[] = [
    { key: 'pvd', label: t(StringKeys.COMPASS_CONFIGURATION )},
    { key: 'emergency', label: t(StringKeys.EMERGENCY_CONFIGURATION) },
  ];

  const renderMobileView = () => (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: COLORS.WHITE,
        boxShadow: 0,
        top: 64,
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ px: 2 }}>
        <FormControl fullWidth size="small">
          <Select
            value={activeTab}
            onChange={(e) => onTabChange(e.target.value as TabKey)}
            sx={{
              backgroundColor: COLORS.WHITE,
              '& .MuiSelect-select': {
                py: 1.5,
              },
            }}
          >
            {tabs.map((tab) => (
              <MenuItem key={tab.key} value={tab.key}>
                <Typography>{tab.label}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Toolbar>
    </AppBar>
  );

  const renderDesktopView = () => (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          top: 65,
          background: '#ffffff',
          height: 'calc(100vh - 65px)',
          overflowY: 'auto',

          // Scrollbar styling
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: '8px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#a0a0a0',
          },

          // Firefox
          scrollbarWidth: 'thin',
          scrollbarColor: '#c1c1c1 transparent',
        },
      }}
    >
      {/* Sidebar header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: '#3e5371',
          boxShadow: 3,
          color: COLORS.WHITE,
          padding: '10px',
          paddingTop: '20px',
          paddingBottom: '20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 300,
            textAlign: 'left',
            textTransform: 'capitalize',
          }}
        >
          {t(StringKeys.CONFIGURATIONS)}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: COLORS.WHITE,
            maxWidth: 210,
            lineHeight: 1.5,
            fontWeight: 300,
          }}
        >
          {t(StringKeys.CONFIGURATION_DESCRIPTION)}
        </Typography>
      </Box>

      {/* Sidebar items */}
      <List sx={{ marginTop: '0px', paddingTop: '0px' }}>
        {tabs.map((tab) => (
          <ListItem
            key={tab.key}
            onClick={() => onTabChange(tab.key)}
            sx={{
              mt: 2,
              cursor: 'pointer',
              color: activeTab === tab.key ? COLORS.SAFEGUARD_BLUE : COLORS.GREY,
              borderRadius: 2,
              backgroundColor: activeTab === tab.key ? '#ededed' : '#ffffff',
              padding: '10px',
              '&:hover': {
                backgroundColor: '#f0f0f0',
              },
            }}
          >
            <ListItemText
              primary={tab.label}
              primaryTypographyProps={{
                fontSize: 14,
                fontWeight: 500,
                textTransform: 'capitalize',
                lineHeight: 1.4,
              }}
            />
          </ListItem>
        ))}
      </List>
    </Drawer>
  );

  return isMobile ? renderMobileView() : renderDesktopView();
};

export default ConfigurationsRouteBar;
